import { get } from 'lodash';
import { array, bool, func, number } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Alert, Col, FormGroup, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import ProgressSelect from '../../generic/ProgressSelect';
import SubmittableForm from '../../generic/SubmittableForm';
import TimestampInfo from '../../generic/TimestampInfo';
import { opportunityShape } from '../../prop-types';
import { rolesEnum } from '../../users/constants';
import storedUserInfo from '../../utils/session-data/user-info';
import {
  salesChannelsEnum,
  stages,
  stagesWithoutClosedWon,
} from '../constants';
import AgreementOpportunities from './AgreementOpportunities';
import OpportunityAdditionalDetails from './OpportunityAdditionalDetails';
import OpportunityBudget from './OpportunityBudget';
import OpportunityDetails from './OpportunityDetails/OpportunityDetails';
import OpportunityUsers from './OpportunityUsers/OpportunityUsers';

const currentUserInfo = storedUserInfo.get();
class OpportunityForm extends Component {
  getInputProps(salesChannel) {
    const { onChange } = this.props;
    return {
      onChange,
      readOnly: !onChange || salesChannel === salesChannelsEnum.SELF_SERVICE,
    };
  }

  render() {
    const {
      model,
      onSubmit,
      newModel,
      hideProgressBar,
      embeddedInAgreement,
      currentAgreementId,
      onChange,
      messages,
      isSalesforceOpp,
    } = this.props;
    const {
      closed,
      created,
      createdBy,
      lastModified,
      lastModifiedBy,
      name,
      salesChannel,
      stage,
      primaryQuoteId,
      owner,
      id,
    } = model;

    const inputProps = this.getInputProps(salesChannel);
    const isSalesChannelSelfService =
      salesChannel === salesChannelsEnum.SELF_SERVICE;
    const isSalesChannelManaged = salesChannel === salesChannelsEnum.MANAGED;
    const isSameSalesUnitAsOwner =
      currentUserInfo?.salesUnit === owner?.salesUnit;
    const hasPcLeadRole = currentUserInfo?.roles.includes(
      rolesEnum.PARTNER_CONSULTANCY_LEAD,
    );

    const warning = messages.filter(item => {
      const oppId = get(item, ['message', 'props', 'warning', 'id']);
      if (item.color === 'warning' && oppId === id) return item;
      return null;
    });

    const warningMessages =
      warning.length > 0 &&
      warning.map(item =>
        get(item, [
          'message',
          'props',
          'warning',
          'response',
          'data',
          'detail',
        ]),
      );
    const latestWarningMessage = warningMessages[warningMessages.length - 1];
    const messageWithLines = latestWarningMessage
      ?.split(';')
      .map(message => <div>{message}</div>);

    return (
      <SubmittableForm onSubmit={onSubmit}>
        {!hideProgressBar && (
          <Row className="mb-5">
            <Col>
              <ProgressSelect
                id="op-stage"
                name="stage"
                resourceName={name}
                options={isSalesforceOpp ? stages : stagesWithoutClosedWon}
                value={stage}
                {...inputProps}
                readOnly={inputProps.readOnly || newModel}
                isSalesforceOpp={isSalesforceOpp}
              />
            </Col>
          </Row>
        )}
        <div className="mb-5">
          {warningMessages.length > 0 && latestWarningMessage && (
            <>
              <div className="mb-2">
                The opportunity contains wrong combinations of target suppliers,
                brands or merchants
              </div>
              <Alert color="warning">{messageWithLines}</Alert>
            </>
          )}
        </div>
        <Row>
          <Col>
            <OpportunityDetails
              model={model}
              embeddedInAgreement={embeddedInAgreement}
              onSubmit={onSubmit}
              isSalesChannelSelfService={isSalesChannelSelfService}
              hasPcLeadRole={hasPcLeadRole}
              isSameSalesUnitAsOwner={isSameSalesUnitAsOwner}
              isSalesforceOpp={isSalesforceOpp}
              {...inputProps}
            />

            <div className="mt-5">
              <FormGroup row>
                <Label sm={4} for="op-agreements">
                  Agreements
                </Label>
                <Col sm={8}>
                  <AgreementOpportunities
                    currentAgreementId={currentAgreementId}
                    opportunity={model}
                    {...inputProps}
                    readOnly={
                      (hasPcLeadRole &&
                        !isSameSalesUnitAsOwner &&
                        isSalesChannelSelfService) ||
                      !onChange
                    }
                  />
                </Col>
              </FormGroup>
            </div>

            <div>
              <OpportunityBudget
                opportunity={model}
                isSalesforceOpp={isSalesforceOpp}
                {...inputProps}
              />
            </div>
          </Col>
          <Col>
            <OpportunityUsers
              model={model}
              isSalesChannelSelfService={isSalesChannelSelfService}
              isSalesChannelManaged={isSalesChannelManaged}
              {...inputProps}
              isSalesforceOpp={isSalesforceOpp}
            />
            <OpportunityAdditionalDetails
              model={model}
              isSalesChannelSelfService={isSalesChannelSelfService}
              hasPcLeadRole={hasPcLeadRole}
              isSameSalesUnitAsOwner={isSameSalesUnitAsOwner}
              embeddedInAgreement={embeddedInAgreement}
              {...inputProps}
              isSalesforceOpp={isSalesforceOpp}
            />
            {embeddedInAgreement && (
              <div className="mb-5">
                {primaryQuoteId && (
                  <FormGroup row>
                    <Label sm={4} for="op-primaryQuoteId">
                      Quote
                    </Label>
                    <Col sm={8}>
                      <Association
                        id="op-primaryQuoteId"
                        name="primaryQuoteId"
                        value={primaryQuoteId}
                        className="form-control-plaintext-without-color-override"
                        type="quote"
                        {...inputProps}
                        readOnly
                      />
                    </Col>
                  </FormGroup>
                )}
              </div>
            )}
            <div>
              <FormGroup row>
                <Col sm={12} className="small d-flex justify-content-end">
                  {!newModel && (
                    <ul className="list-inline m-0">
                      <li className="list-inline-item">
                        <TimestampInfo
                          timestamp={created}
                          actionName="created"
                          author={createdBy}
                        />
                      </li>
                      <li className="list-inline-item">
                        <TimestampInfo
                          timestamp={lastModified}
                          actionName="lastModified"
                          author={lastModifiedBy}
                        />
                      </li>
                      {closed && (
                        <li className="list-inline-item">
                          <TimestampInfo
                            timestamp={closed}
                            actionName="closed"
                          />
                        </li>
                      )}
                    </ul>
                  )}
                </Col>
              </FormGroup>
            </div>
          </Col>
        </Row>
      </SubmittableForm>
    );
  }
}

OpportunityForm.propTypes = {
  model: opportunityShape.isRequired,
  onChange: func,
  onSubmit: func,

  newModel: bool,
  hideProgressBar: bool,
  embeddedInAgreement: bool,
  currentAgreementId: number,
  messages: array, // eslint-disable-line react/forbid-prop-types
  isSalesforceOpp: bool,
};

OpportunityForm.defaultProps = {
  onChange: undefined,
  onSubmit: undefined,

  newModel: false,
  hideProgressBar: false,
  embeddedInAgreement: false,
  currentAgreementId: undefined,
  messages: [],
  isSalesforceOpp: false,
};

const mapStateToProps = state => ({
  messages: state.flashMessages,
});

export default connect(mapStateToProps, null)(OpportunityForm);
